import type { Values } from '@web-platform/shared-universal-types-utils/src/utils';

export const SERVICE = {
  DFP: 'dfp',
  DS: 'SPONSORED_SERVICE',
} as const;

export type Service = Values<typeof SERVICE>;

export const ARS_USER_PLATFORMS = {
  MOBILE_WEB: 'MOBILE_WEB',
  DESKTOP_WEB: 'DESKTOP_WEB',
} as const;

export type ArsUserPlatform = Values<typeof ARS_USER_PLATFORMS>;

// ARS has no tablet platform value; tablet is treated as desktop.
export const getArsUserPlatform = (isMobilePhone: boolean): ArsUserPlatform =>
  isMobilePhone ? ARS_USER_PLATFORMS.MOBILE_WEB : ARS_USER_PLATFORMS.DESKTOP_WEB;

export const PLACEMENT_IDS = {
  BOOKING_PANEL: 'mobile.flights.booking-panel/top',
  BOOKING_PANEL_DESKTOP: 'desktop.flights.booking-panel/top',
  HOMEPAGE_AD_CARD: 'mobile.flights.homepage/slot',
  DAY_VIEW: 'desktop.flights.dayview/global',
  COMBINED_EXPLORE: 'mobile.flights.dayview/global',
} as const;
export const HOMEPAGE_AD_CARD_ADSLOT_ID = 'adslot-138c730c';
export const HOMEPAGE_NAV_CARD_1_SLOT_ID = 'adslot-navcard1';
export const HOMEPAGE_NAV_CARD_2_SLOT_ID = 'adslot-navcard2';
export const HOMEPAGE_NAV_CARD_3_SLOT_ID = 'adslot-navcard3';
export const HOMEPAGE_NAV_CARD_4_SLOT_ID = 'adslot-navcard4';
export const HOMEPAGE_NAV_CARD_5_SLOT_ID = 'adslot-navcard5';
export const HOMEPAGE_NAV_CARD_6_SLOT_ID = 'adslot-navcard6';
export const HOMEPAGE_NAV_CARD_7_SLOT_ID = 'adslot-navcard7';
export const BOOKING_PANEL_AD_SLOT_ID = 'adslot-1';
export const PAGE_TYPES = {
  HOME: 'flights.home',
  DAY_VIEW: 'flights.dayview',
  BOOKING_PANEL: 'fligths.booking-panel',
} as const;
export const NONE_AVAILABLE = 'NONE_AVAILABLE';
export const EFS_GRAPHIC_PROMO_EVERYWHERE_CTA_URL = 'EXPLORE_EVERYWHERE';
export const PLATFORM_INTERVAL_BETWEEN_ADS = 5;
export const ERROR_BOUNDARY_PREFIX_QUARK = 'AdsQuark';
export const ERROR_BOUNDARY_PREFIX_DELOREAN = 'AdsDelorean';

export const ADVERT_FORMAT_TYPES = {
  INLINE: 'fmt.flightsInline',
  BANNER: 'fmt.flightsBanner',
  BRAND_BANNER: 'fmt.flightsInlineBrandBanner',
  COMPETITOR_BRAND_BANNER: 'fmt.flightsInlineCompetitorBrandBanner',
  ITINERARY_INLINE_PLUS: 'fmt.flightsItineraryInlinePlus',
  UNPRICED_BRAND_INLINE: 'fmt.flightsUnpricedBrandInline',
  PRICED_BRAND_INLINE: 'fmt.flightsPricedBrandInline',
  PACKAGES_INLINE: 'fmt.packagesInline',
  ALTERNATE_DATES_INLINE: 'fmt.flightsAlternateDatesInline',
  PACKAGES_PRICED: 'fmt.packagesPriced',
  HOMEPAGE_AD_CARD: 'fmt.homepageHeroSponsored',
  HOMEPAGE_AD_VIDEO: 'fmt.homepageVideo',
  FLEXIBLE_SEARCH: 'fmt.flexibleSearch',
  NAV_CARD: 'fmt.navCard',
  CAR_HIRE_BOTTOM_BANNER: 'fmt.bottomCarHireBanner',
  FILTERED_EXPLORE_AIRLINE: 'fmt.filteredExploreAirline',
} as const;

// Brand advertising formats hidden while a Sponsored Airlines V2 filter is active,
// so competing brand ads don't appear inside a sponsored-airline-filtered day view.
export const SAV2_SUPPRESSED_ADVERT_FORMATS: ReadonlySet<string> = new Set([
  ADVERT_FORMAT_TYPES.PRICED_BRAND_INLINE,
  ADVERT_FORMAT_TYPES.UNPRICED_BRAND_INLINE,
  ADVERT_FORMAT_TYPES.BRAND_BANNER,
  ADVERT_FORMAT_TYPES.COMPETITOR_BRAND_BANNER,
]);

export const UNSUPPORTED_AD = 'unsupportedAd';

export const FLIGHTS_INLINE_TYPES = {
  BRAND: 'brand',
  ITINERARY: 'itinerary',
} as const;

export const ADVERT_ITINERARY_SEARCH_TYPES = {
  CHEAPEST: 'cheapest',
  BEST: 'best',
  UNPRICED: 'unpriced',
} as const;

export const ADVERT_ITINERARY_SELECTION_TYPES = {
  CHEAPEST: 'cheapest',
  BEST: 'best',
} as const;

export const SPONSORED_DAY_VIEW_TYPES = {
  FILTERED: 'filteredDayView',
  FILTERED_V2: 'filteredDayViewV2',
  DESTINATION: 'destinationDayView',
  DESTINATION_V2: 'destinationDayViewV2',
} as const;

export const ADVERT_ENGAGEMENT_TYPES = {
  OPEN: 'open',
  CLOSE: 'close',
  SECONDARY_CTA: 'secondary_cta',
  VIDEO_OPEN: 'video_open',
} as const;

export const SPONSORED_SERVICE_PRIORITY = {
  PREMIUM: 'PREMIUM',
  STANDARD: 'STANDARD',
  HOUSE: 'HOUSE',
  NETWORK: 'NETWORK',
  UNSET: 'UNSET_PRIORITY_TYPE',
} as const;

export const COMPETITOR_BRAND_BANNER_REDIRECT_TYPES = {
  CITY_GUIDE_PAGE: 'city_guide_page',
  DAY_VIEW: 'day_view',
} as const;

export const EXPLORE_INLINE_TYPES = {
  DOUBLE_DISPLAY: 'DOUBLE_DISPLAY_ADVERT',
  SINGLE_DISPLAY: 'STANDARD_DISPLAY_ADVERT',
} as const;

export const ADVERT_PARTNER_TYPE = {
  OTA: 'ota',
  AIRLINE: 'airline',
} as const;

export const ADVERT_CARRIERS_SELECTION_CRITERIA = {
  ALL: 'all',
  EXCLUDE: 'exclude',
  INCLUDE: 'include',
} as const;

// This value is used in the partner field to force an unpriced advert in legacy ads coming from GAM
export const DISABLE_PRICE_PARTNER_VALUE = 'override';
export const HOMEPAGE_VIDEO_TIMEOUT_IN_MILLIS = 7000;
export const DEEPLINK_PLACEHOLDER = '--deeplink--';
export const ENCODED_DEEPLINK_PLACEHOLDER = '--encoded_deeplink--';
export const STREVDA_SLOT_FLIGHTS_INLINE_SERVICE_DFP = SERVICE.DFP;
export const STREVDA_FLIGHTS_CONTRACT_EVENT_NAME = 'sponsored-slots';

export const DAY_VIEW_PLACEMENTS = {
  DESKTOP: 'desktop.flights.dayview/inline',
  MOBILE: 'mobile.flights.dayview/inline',
} as const;

export const DAYVIEW_INLINE_AD_CLICK_SMART_METRIC = 'Dayview_Inline_Ad_Clicked';
export const COMBINED_RESULTS_INLINE_AD_CLICK_SMART_METRIC = 'CombinedResults_Inline_Ad_Clicked';

export const HOMEPAGE_FALLBACK_CARD_REASONS = {
  AD_RESPONSE_FAILURE: 'AD_RESPONSE_FAILURE',
  AD_RESPONSE_SERVER_NO_ADVERT_AVAILABLE: 'AD_RESPONSE_SERVER_NO_ADVERT_AVAILABLE',
  AD_RESPONSE_TIMEOUT: 'AD_RESPONSE_TIMEOUT',
  AD_CONTENT_BROKEN: 'AD_CONTENT_BROKEN',
  AD_DISABLED: 'AD_DISABLED',
} as const;

export const HOMEPAGE_FALLBACK_CARD_EXP_VARIANTS = {
  VARIANT_A: 'VARIANT_A',
  VARIANT_B: 'VARIANT_B',
} as const;

export const TRANSFER_TYPES = {
  SELF_TRANSFER: 'SELF_TRANSFER',
  PROTECTED_SELF_TRANSFER: 'PROTECTED_SELF_TRANSFER',
  MANAGED: 'MANAGED',
} as const;
